import { CommonModule } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { DsButtonV2Component, DsContentSheetComponent, DsIconComponent, DsInputFileComponent, DsModalBottomSheetComponent } from "@customdesignsystem";

/** Documento persistido en StorageService para restaurar estado al navegar. */
export interface DocumentoCacheado {
  status: string;
  fileName: string;
  mimeType: string;
  fileBase64: string;
  uploadId?: string;
}


@Component({
  selector: "app-pi-document-status",
  templateUrl: "./pi-document-status.component.html",
  styleUrls: ["./pi-document-status.component.scss"],
  imports: [CommonModule, DsInputFileComponent, DsModalBottomSheetComponent, DsButtonV2Component, DsIconComponent, DsContentSheetComponent],
  standalone: true
})
export class PiDocumentStatusComponent implements OnDestroy {
  @Input() type: string;
  @Input() title: string;
  @Input() status: string;
  @Input() errorMessage: string = 'Hubo un error con la carga de este documento. Por favor, inténtalo nuevamente.';
  @Output() documentType = new EventEmitter<string>();
  @Output() fileSelected = new EventEmitter<any>();
  @Output() cancelQuery = new EventEmitter<boolean>();
  @Output() deleteDoc = new EventEmitter<any>();
  @ViewChild(DsInputFileComponent)
  private readonly dsInputFile?: DsInputFileComponent;
  errorCustomFile = false;
  selectedFileName = "";

  /** Restaura nombre y vista previa de un documento cacheado (al volver a esta página). */
  @Input() set cachedDocument(value: DocumentoCacheado | null | undefined) {
    if (!value?.fileBase64) {
      return;
    }
    this.selectedFileName = value.fileName;
    this.errorCustomFile = false;
    const file = this.base64ToFile(value.fileBase64, value.fileName, value.mimeType);
    if (file) {
      this.setPreview(file);
    }
  }

  sheetVisible = false;
  sheetReviewVisible = false;
  previewUrl: string | null = null;
  previewResourceUrl: SafeResourceUrl | null = null;
  previewType: "image" | "pdf" | null = null;

  constructor(private readonly sanitizer: DomSanitizer) { }

  ngOnDestroy(): void {
    this.clearPreview();
  }

  setDocumentType() {
    this.documentType.emit(this.type);
  }

  onFileSelected(event: any): void {
    console.log("onFileSelected : ", event);
    const maxSize10Mb = 10000000;
    const file = event.target.files[0];
    if (
      file?.size <= maxSize10Mb &&
      (file?.type === "application/pdf" || file?.type === "image/jpeg")
    ) {
      this.errorCustomFile = false;
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64 = e.target.result.split(";base64,");
        const documentType = this.type;
        const payload = {
          documentType,
          fileStringBase64: base64[1],
        };
        this.fileSelected.emit(payload);
      };
      reader.readAsDataURL(file);
    } else {
      this.errorCustomFile = true;
    }
  }

  onFileSelectedDS(files: readonly File[]): void {
    const file = files[0];
    if (!file) {
      return;
    }

    const maxSize10Mb = 10000000;
    if (
      file.size > maxSize10Mb ||
      (file.type !== "application/pdf" && file.type !== "image/jpeg")
    ) {
      this.errorCustomFile = true;
      return;
    }

    this.errorCustomFile = false;
    this.setPreview(file);
    const documentType = this.type;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string" || !result.includes(";base64,")) {
        this.errorCustomFile = true;
        console.error("Error al leer el documento: formato de archivo inesperado");
        return;
      }
      this.fileSelected.emit({
        documentType,
        fileStringBase64: result.split(";base64,")[1],
        fileName: file.name,
        mimeType: file.type,
      });
    };
    reader.onerror = () => {
      this.errorCustomFile = true;
      console.error("Error al leer el documento", reader.error);
    };
    reader.readAsDataURL(file);
  }

  cancel() {
    this.cancelQuery.emit(true);
  }

  confirmDeleteDocument() {
    this.sheetVisible = true;
  }

  delDocument() {
    this.sheetVisible = false;
    this.sheetReviewVisible = false;
    this.clearPreview();
    let documentType = this.type;
    let payload = { documentType }
    this.deleteDoc.emit(payload);
  }

  get dsState(): 'default' | 'uploading' | 'success' | 'error' {
    switch (this.status) {
      case 'VALIDATION_IN_PROGRESS':
        return 'uploading';
      case 'SUCCESS':
        return 'success';
      case 'ERROR':
        return 'error';
      default:
        return 'default';
    }
  }

  get dsDescripcion() {
    if (this.status === 'ERROR') {
      return this.errorMessage;
    } else {
      return 'JPG, PNG o PDF · Máx 10 MB';
    }
  }

  reviewDocument() {
    this.sheetReviewVisible = true;
  }

  private setPreview(file: File): void {
    this.clearPreview();
    this.previewUrl = URL.createObjectURL(file);
    this.previewType = file.type === "application/pdf" ? "pdf" : "image";
    this.previewResourceUrl = this.previewType === "pdf"
      ? this.sanitizer.bypassSecurityTrustResourceUrl(this.previewUrl) // NOSONAR: Safe blob URL generated by URL.createObjectURL from a validated file, not from user-controlled input
      : null;
  }

  private clearPreview(): void {
    if (this.previewUrl) {
      URL.revokeObjectURL(this.previewUrl);
    }
    this.previewUrl = null;
    this.previewResourceUrl = null;
    this.previewType = null;
  }

  private base64ToFile(base64: string, fileName: string, mimeType: string): File | null {
    try {
      const binary = atob(base64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return new File([bytes], fileName, { type: mimeType });
    } catch (error) {
      console.error("Error al restaurar el documento desde caché", error);
      return null;
    }
  }

  public retryUpload(): void {
    this.errorCustomFile = false;
    this.dsInputFile?.openFileDialog();
  }
}
